// src/redux/slices/drawers/clinicalNotesDrawerSlice.ts (Enhanced)
import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { patients } from "lobbie";
import { IFormHelper } from "src/components/pages/staff/patients/dep/AccountPatientForms";
import { BASE_API_URL, BASE_SECURE_ROUTE } from "src/constants/api";
import { handleBackendHttpError } from "src/redux/handleBackendHttpError";
import { RootState } from "src/redux/store";
import { generateUUID, notify } from "src/utils";
import { createDrawerSlice, IBaseDrawerState } from "./createDrawerSlice";

export interface IClinicalNotesDrawerState extends IBaseDrawerState {
    show: boolean;
    isLoading: boolean;
    error: string | null;

    forms: IFormHelper[];
    selectedFormId: number | undefined;
    display: string;
    appointmentId?: number;
    accountPatientId?: number;
    accountPatient?: patients.IAccountPatient; // Added patient data
}

export const initialStateClinicalNotesDrawer: IClinicalNotesDrawerState = {
    drawerHistoryInstanceId: "clinicalNotesDrawer",
    show: false,
    isLoading: false,
    error: null,

    forms: [],
    selectedFormId: undefined,
    display: "forms",
    appointmentId: undefined,
    accountPatientId: undefined,
    accountPatient: undefined,
};

export const fetchAndOpenClinicalNotesDrawer = createAsyncThunk<
    { forms: IFormHelper[]; patient: patients.IAccountPatient },
    { appointmentId: number; accountPatientId: number },
    { state: RootState; rejectValue: string }
>(
    "clinicalNotesDrawer/fetchAndOpenClinicalNotesDrawer",
    async ({ appointmentId, accountPatientId }, { rejectWithValue }) => {
        try {
            // Fetch forms from the appointment
            const formsResponse = await axios.get(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/appointments/${appointmentId}/forms`,
                { withCredentials: true },
            );
            const allForms = formsResponse.data as IFormHelper[];
            // Filter out non-staff-only forms
            const staffOnlyForms = allForms.filter((form) => form.staffOnly === true);
            const patientResponse = await axios.get(
                `${BASE_API_URL}/${BASE_SECURE_ROUTE}/staff/account_patients/${accountPatientId}`,
                { withCredentials: true },
            );
            return {
                forms: staffOnlyForms,
                patient: patientResponse.data as patients.IAccountPatient,
            };
        } catch (error: any) {
            handleBackendHttpError(error);
            return rejectWithValue(error?.message || "Failed to load clinical forms");
        }
    },
);

// NOTE: this slice is setup to be used in the drawer stack system but it hasn't been integrated quite yet
const clinicalNotesDrawerSlice = createDrawerSlice({
    name: "clinicalNoteDrawer",
    initialState: initialStateClinicalNotesDrawer,
    reducers: {
        openClinicalNote: {
            prepare: (params: {
                appointmentId?: number;
                accountPatientId?: number;
                forms?: IFormHelper[];
                patientName?: string;
                formId?: number;
            }) => ({
                payload: {
                    drawerHistoryInstanceId: `clinicalNotes-${generateUUID()}`,
                    ...params,
                },
            }),
            reducer: (state, action) => {
                // Filter forms to only include staff-only forms if they were provided
                const forms = action.payload.forms
                    ? action.payload.forms.filter((form) => form.staffOnly === true)
                    : [];

                return {
                    ...initialStateClinicalNotesDrawer,
                    drawerHistoryInstanceId: action.payload.drawerHistoryInstanceId,
                    show: true,
                    appointmentId: action.payload.appointmentId,
                    accountPatientId: action.payload.accountPatientId,
                    patientName: action.payload.patientName,
                    forms: forms,
                    selectedFormId: action.payload.formId,
                };
            },
        },
        hideClinicalNotesDrawer: (state) => {
            state.show = false;
        },
        setSelectedFormId: (state, action: PayloadAction<number | undefined>) => {
            state.selectedFormId = action.payload;
            state.display = "forms";
        },
        setDisplay: (state, action: PayloadAction<string>) => {
            state.display = action.payload;
        },
        markFormComplete: (state, action: PayloadAction<number>) => {
            const formIndex = state.forms.findIndex((form) => form.id === action.payload);
            if (formIndex !== -1) {
                state.forms[formIndex].complete = true;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAndOpenClinicalNotesDrawer.pending, (state) => {
                return {
                    ...initialStateClinicalNotesDrawer,
                    isLoading: true,
                    error: null,
                    show: true,
                    drawerHistoryInstanceId: `clinicalNotes-${generateUUID()}`,
                };
            })
            .addCase(fetchAndOpenClinicalNotesDrawer.fulfilled, (state, action) => {
                state.isLoading = false;
                state.forms = action.payload.forms;
                // open the first form by default
                if (state.forms.length > 0) {
                    state.selectedFormId = state.forms[0].id;
                }
                state.accountPatient = action.payload.patient;
            })
            .addCase(fetchAndOpenClinicalNotesDrawer.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || "Failed to load clinical forms";
                notify({
                    level: "error",
                    title: "Error",
                    message: state.error,
                });
            });
    },
});

export const {
    openClinicalNote,
    hideClinicalNotesDrawer,
    setSelectedFormId,
    setDisplay,
    markFormComplete,
} = clinicalNotesDrawerSlice.actions;

export default clinicalNotesDrawerSlice.reducer;
